import { Box, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { CustomerDto, CustomerGetPaginatedDto } from "@/core/api/generated/v1";

import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";

export type CustomerAutocompleteOption = BaseAutocompleteOption<CustomerDto>;

export const getCustomerOptionTitle = (data: CustomerDto): string =>
  StringHelper.join(
    [
      data.name || data.contact?.personName?.name,
      `(${enumService.getEnumValueName("CustomerType", data.type)})`,
    ],
    " ",
    {
      skipEmpty: true,
    },
  );

export const customerToOption = (data: CustomerDto): CustomerAutocompleteOption => ({
  id: data.id!,
  title: getCustomerOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface CustomerAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<CustomerDto> {
  searchFilters?: Partial<Omit<CustomerGetPaginatedDto, "search" | "includeIds">>;
}

export default function CustomerAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: CustomerAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={customerToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.customersApi.apiV1CustomersGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          customerGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          customerGetPaginatedDto: {
            ...params.customerGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Customer'
      placeholder='Search for customer...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='customer' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      <Box>{option.data?.contact?.email}</Box>
                      <Box>
                        <InlineApiEnumValue type='CustomerType' value={option.data?.type} />
                      </Box>
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
